import { useState, useEffect, useRef, useCallback } from 'react';
import { chatAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { MessageCircle, X, Send, Loader2, User, Minimize2 } from 'lucide-react';

export function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Load chat history when first opened
  useEffect(() => {
    if (open && !historyLoaded) {
      setLoadingHistory(true);
      chatAPI.history(50).then(res => {
        const history = res.data || [];
        if (history.length > 0) {
          setMessages(history.map((m, i) => ({
            id: `history-${i}`,
            role: m.role,
            content: m.content,
          })));
        }
        setHistoryLoaded(true);
      }).catch(() => {
        // Failed to load history, continue with empty
        setHistoryLoaded(true);
      }).finally(() => {
        setLoadingHistory(false);
      });
    }
  }, [open, historyLoaded]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { id: `user-${Date.now()}`, role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await chatAPI.send(text);
      const bubbles = res.data.bubbles || [res.data.reply];
      // Add each bubble as a separate message with staggered delay
      for (let i = 0; i < bubbles.length; i++) {
        if (i > 0) await new Promise(r => setTimeout(r, 400));
        const assistantMsg = { id: `ai-${Date.now()}-${i}`, role: 'assistant', content: bubbles[i] };
        setMessages(prev => [...prev, assistantMsg]);
      }
    } catch (err) {
      const errorMsg = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-chat-toggle"
          className="fixed bottom-20 right-6 z-[300] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors duration-150 active:scale-95"
          style={{ boxShadow: '0 4px 20px rgba(25,82,89,0.35)' }}
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-[300] w-[380px] max-w-[calc(100vw-48px)]"
          style={{ maxHeight: 'calc(100vh - 140px)' }}
          data-testid="ai-chat-panel"
        >
          <Card className="flex flex-col overflow-hidden shadow-2xl border" style={{ height: '520px' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-card shrink-0">
              <div className="flex items-center gap-2.5">
                <img src="/images/chatbot-icon.png" alt="Funnely AI" className="h-9 w-9 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-semibold leading-none">Funnely AI</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Your creator sidekick</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setOpen(false)}
                  data-testid="ai-chat-close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4">
              <div className="py-4 space-y-4">
                {/* Welcome message */}
                {messages.length === 0 && !loadingHistory && (
                  <div className="text-center py-8 space-y-3">
                    <img src="/images/chatbot-icon.png" alt="Funnely AI" className="h-14 w-14 rounded-full object-cover mx-auto" />
                    <div>
                      <p className="font-semibold text-sm">Hey! I'm Funnely AI</p>
                      <p className="text-xs text-muted-foreground mt-1 max-w-[260px] mx-auto leading-relaxed">
                        Ask me anything — from how to use Funnely to tips on selling more. Tulis BM pun boleh!
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2 pt-2">
                      {['How to create a product?', 'How to publish landing page?', 'Macam mana nak track sales?'].map((q, i) => (
                        <button
                          key={i}
                          onClick={() => { setInput(q); }}
                          className="text-xs px-3 py-1.5 rounded-full bg-accent text-accent-foreground hover:bg-accent/80 transition-colors duration-150"
                          data-testid={`ai-chat-suggestion-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {loadingHistory && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <img src="/images/chatbot-icon.png" alt="" className="h-7 w-7 rounded-full object-cover shrink-0 mt-0.5" />
                    )}
                    <div
                      className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : msg.isError
                            ? 'bg-destructive/10 text-destructive rounded-bl-md'
                            : 'bg-muted text-foreground rounded-bl-md'
                      }`}
                      data-testid={`ai-chat-message-${msg.role}`}
                    >
                      {msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                ))}

                {sending && (
                  <div className="flex gap-2.5 justify-start">
                    <img src="/images/chatbot-icon.png" alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
                    <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="px-4 py-3 border-t bg-card shrink-0">
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about Funnely..."
                  disabled={sending}
                  className="flex-1 h-10 text-sm"
                  data-testid="ai-chat-input"
                />
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={!input.trim() || sending}
                  className="h-10 w-10 shrink-0"
                  data-testid="ai-chat-send"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                Powered by AI &middot; Understands Bahasa Melayu
              </p>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
